import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  InputGroup,
  ModalFooterControlsContainer,
} from '@/features/modals/components';
import {
  QueryCategoriesSection,
  useCategoriesSelect,
} from '@/views/QueryBuilder/subcomponents/TopControls/ModalUpdateSavedQuery';
import { GeneralStylesWidgetContainer } from '@/features/savedQueriesWidget/GeneralStylesWidgetContainer';
import { SavedQueriesWidget } from '@/features/savedQueriesWidget/SavedQueriesWidget';
import { SAVE_MODAL } from '@/features/savedQueriesWidget/widgetRenderContext.constants';
import { Button, TrevorIcon } from '@trevor/ui-components';
import { SavedQuery } from '@/types';
import { useModalClose } from '@/features/modals/modalSlice';
import LayerHelper from '@/util/LayerHelper';
import { useSelector } from 'react-redux';
import {
  getChartingOptions,
  getQueryBuilderSteps,
  getSelectedLayerId,
} from '@/store-selectors';
import { selectMetricsDimensionConfiguration } from '@/features/queryMetricsDimensionsConfig/metricConfigSlice';
import { useReplaceSavedQueryMutation } from '@/features/query-builder/savedQueriesApiSlice';
import { useDataPresentationParam, useDatasourceId } from '@/hooks';

const QUANTITY_TO_TEXT_MAP: Record<
  'ONE' | 'FEW',
  { title: string; text: string }
> = {
  ONE: {
    title: 'another query depends',
    text: 'that query',
  },
  FEW: {
    title: 'other queries depend',
    text: 'those queries',
  },
};

const SavedQueriesWidgetContainer = styled(GeneralStylesWidgetContainer)`
  padding: 20px;
`;

const WarningBanner = styled.div`
  --h-space: 12px;
  --font-size: 14px;
  --line-height: 21px;

  background-color: #ffdac2;
  display: flex;
  padding: var(--h-space) 20px;
  width: 100%;

  & > svg {
    margin-right: var(--h-space);
  }

  & > div > h3 {
    font-weight: bold;
    font-size: var(--font-size);
    line-height: var(--line-height);
    margin: 0;
  }

  & > div > span {
    display: block;
    font-size: var(--font-size);
    line-height: var(--line-height);
    margin-top: 4px;
  }
`;

const SelectedQueryButton = styled.button`
  font-size: 14px;
  color: #333;
  background-color: #fff;
  padding: 6px 12px;
  height: 45px;
  border: 1px solid #ccc;
  border-radius: var(--radius-4);
`;

const DependatnQueriesWarningBanner: React.FC<{
  selectedQuery: Maybe<SavedQuery>;
}> = ({ selectedQuery }) => {
  const dependantQueries = selectedQuery
    ? [
        ...new Set([
          ...selectedQuery.dependantQuerySeriesIds,
          ...selectedQuery.dependantJoinSeriesIds,
        ]),
      ]
    : [];

  const numberOfDependantQueries = dependantQueries.length;

  if (dependantQueries.length === 0) return null;

  const warningWordings =
    QUANTITY_TO_TEXT_MAP[numberOfDependantQueries === 1 ? 'ONE' : 'FEW'];

  return (
    <div style={{ padding: '20px' }}>
      <WarningBanner>
        <TrevorIcon name="warning" size="md" $color="#F56200" />
        <div>
          <h3>
            {numberOfDependantQueries} {warningWordings.title} on this query
          </h3>
          <span>
            If you replace it, {warningWordings.text} will be updated too. Are
            you sure you want to proceed?
          </span>
        </div>
      </WarningBanner>
    </div>
  );
};

const useCurrentQueryData = () => {
  const steps = useSelector(getQueryBuilderSteps);
  const selectedStepId = useSelector(getSelectedLayerId);
  const chartConfiguration = useSelector(getChartingOptions);
  const metricsDimensionConfiguration = useSelector(
    selectMetricsDimensionConfiguration,
  );

  const layerHelper = new LayerHelper(steps, selectedStepId);
  const activeSteps = layerHelper.activeLayers();
  const inactiveSteps = layerHelper.inactiveLayers();

  return {
    chartConfiguration,
    activeSteps,
    inactiveSteps,
    metricsDimensionConfiguration,
  } as const;
};

export const ReplaceExistingQuery: React.FC = () => {
  const navigate = useNavigate();
  const datasourceId = useDatasourceId();
  const dataPresentation = useDataPresentationParam();
  const handleClose = useModalClose();
  const currentQueryData = useCurrentQueryData();
  const [selectedQuery, setSelectedQuery] = React.useState<Maybe<SavedQuery>>();
  const [categories, setCategories] = useCategoriesSelect();
  const [replaceSavedQuery, { isLoading }] = useReplaceSavedQueryMutation();

  const handleSave = async () => {
    if (!selectedQuery) return;

    const result = await replaceSavedQuery({
      datasourceId,
      seriesId: selectedQuery.series,
      payload: {
        categories,
        ...currentQueryData,
      },
    });

    // TODO: add a proper error handling,
    if (!('data' in result)) return;

    const createdSavedQuerySeriesId = result.data.seriesId;
    const dataPresentationPath = dataPresentation ? `/${dataPresentation}` : '';
    const nextUrl = `/datasources/${datasourceId}/saved-queries/${createdSavedQuerySeriesId}${dataPresentationPath}`;

    // Navigate within the next loop tick when RTK-Query cache is already updated.
    setTimeout(navigate, 0, nextUrl);

    handleClose();
  };

  return (
    <>
      <DependatnQueriesWarningBanner selectedQuery={selectedQuery} />
      <InputGroup>
        <label>Replace query</label>
        {selectedQuery ? (
          <SelectedQueryButton onClick={() => setSelectedQuery(undefined)}>
            {selectedQuery.name}
          </SelectedQueryButton>
        ) : (
          <input type="text" disabled />
        )}
      </InputGroup>

      <QueryCategoriesSection onUpdate={setCategories} />

      {!selectedQuery && (
        <SavedQueriesWidgetContainer>
          <SavedQueriesWidget
            onSelectQuery={setSelectedQuery}
            mode={SAVE_MODAL}
          />
        </SavedQueriesWidgetContainer>
      )}

      <ModalFooterControlsContainer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          id="btnModalSubmit"
          onClick={handleSave}
          disabled={!selectedQuery || isLoading}
        >
          Submit
        </Button>
      </ModalFooterControlsContainer>
    </>
  );
};
